/**
 * =============================================================================
 * InternalNoteBubble — Balão de "Mensagem privada" (nota interna) no chat
 * Versão: 1.0.0 (2026-08-25)
 *
 * Descrição:
 *   Renderiza uma nota interna da equipe dentro da timeline do chat.
 *   A nota é gravada como mensagem de sistema (`sender_type = 'system'`) com
 *   `metadata.kind = 'internal_note'`, portanto NUNCA é enviada ao provider
 *   (WhatsApp/Instagram/etc) nem ao widget do visitante — reaproveita 100% a
 *   estrutura já existente de mensagens de sistema.
 *
 *   As @menções são armazenadas no texto no formato `@[Nome](user_id)` e
 *   destacadas visualmente aqui.
 * =============================================================================
 */
import React, { useMemo } from 'react';
import { Lock } from 'lucide-react';
import { SmartAvatar } from '@/components/ui/smart-avatar';

interface InternalNoteBubbleProps {
  /** Conteúdo bruto da nota (pode conter menções `@[Nome](uuid)`). */
  content: string;
  /** Nome do autor da nota (vem de metadata.author_name). */
  authorName?: string;
  /** Avatar do autor (opcional). */
  authorAvatar?: string | null;
  /** Horário formatado para exibição. */
  time: string;
}

/** Regex das menções no formato `@[Nome](uuid)`. */
const MENTION_REGEX = /@\[([^\]]+)\]\(([0-9a-fA-F-]{36})\)/g;

/**
 * Converte o texto da nota em nós React, destacando as @menções.
 * Nunca injeta HTML (sem dangerouslySetInnerHTML) — texto puro, seguro por padrão.
 */
function renderContentWithMentions(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const regex = new RegExp(MENTION_REGEX);

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <span
        key={`${match.index}-${match[2]}`}
        // Mesmo padrão visual das menções nos comentários de andamento (azul de link)
        className="font-medium text-blue-600 dark:text-blue-400"
      >
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

/** Balão amarelo de nota interna, centralizado como as demais mensagens de sistema. */
export function InternalNoteBubble({
  content,
  authorName,
  authorAvatar,
  time,
}: InternalNoteBubbleProps) {
  const parts = useMemo(() => renderContentWithMentions(content || ''), [content]);

  return (
    <div className="w-full flex justify-center py-2">
      <div className="max-w-[85%] md:max-w-[70%] w-full md:w-auto rounded-xl border border-amber-300/70 dark:border-amber-600/40 bg-amber-100/90 dark:bg-amber-500/10 px-3 py-2 shadow-sm">
        {/* Cabeçalho: autor + selo de privacidade */}
        <div className="flex items-center gap-2 mb-1">
          <SmartAvatar
            src={authorAvatar || undefined}
            name={authorName || 'Sistema'}
            size="xs"
            alt={authorName || 'Sistema'}
          />
          <span className="text-[11px] font-medium text-amber-900 dark:text-amber-200 truncate">
            {authorName || 'Nota interna'}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] text-amber-800/80 dark:text-amber-300/80">
            <Lock className="h-3 w-3" />
            Mensagem privada
          </span>
          <span className="ml-auto text-[10px] text-amber-800/70 dark:text-amber-300/70">
            {time}
          </span>
        </div>

        {/* Corpo da nota (texto puro + menções destacadas) */}
        <p className="text-xs whitespace-pre-wrap break-words text-amber-950 dark:text-amber-100">
          {parts}
        </p>
      </div>
    </div>
  );
}

export default InternalNoteBubble;
